/**
 * The car radio's stations: the real ones of wherever the car is, from the
 * Radio Browser directory (radio-browser.info), a free, open list of
 * internet radio streams with each station's country and, for many, where
 * it is.
 *
 * The stations that are local come first — within a drive of here, say P4
 * Göteborg in Gothenburg — then the country's most listened to. A browser
 * plays an MP3, AAC or Ogg stream from an `<audio>` element, but only one
 * served over HTTPS on an HTTPS page, so only those are kept. The directory
 * lists many stations several times over (two bitrates, a name spelled two
 * ways); each is kept once.
 *
 * Pure: no fetch, no DOM. `loaders/radioService.ts` asks the directory.
 */

import type { Country } from '../geo/country.js';

/** One station in the directory, as its JSON has it: only what is used here. */
export interface DirectoryStation {
  name: string;
  url_resolved: string;
  codec: string;
  countrycode: string;
  geo_lat: number | null;
  geo_long: number | null;
  clickcount: number;
  lastcheckok: number;
}

export interface Station {
  name: string;
  url: string;
  /** Within `LOCAL_KM` of where the list was made. */
  local: boolean;
}

/** How far off a station is still local, kilometres. */
export const LOCAL_KM = 80;
/** Stations on the dial, at most; and of them local, at most. */
export const MOST_STATIONS = 8;
const MOST_LOCAL = 4;
/** The longest a station's name is shown, characters. */
const NAME_LENGTH = 34;

const PLAYABLE = /^(mp3|aac|aac\+|ogg|opus)$/i;

/** The directory's code for a country (ISO 3166 alpha-2). */
export function directoryCode(country: Country): string {
  return country === 'uk' ? 'GB' : country.toUpperCase();
}

/** Kilometres between two places, near enough. */
function km(lat0: number, lon0: number, lat1: number, lon1: number): number {
  const dLat = lat1 - lat0, dLon = (lon1 - lon0) * Math.cos(((lat0 + lat1) / 2) * Math.PI / 180);
  return Math.hypot(dLat, dLon) * 111.2;
}

/** A station's name as shown: trimmed, its spaces single, cut short if long. */
export function stationName(raw: string): string {
  const name = raw.replace(/\s+/g, ' ').trim();
  return name.length > NAME_LENGTH ? `${name.slice(0, NAME_LENGTH - 1).trim()}…` : name;
}

/** What makes two of the directory's entries the same station: "Sveriges Radio - P3" is "Sveriges Radio P3". */
const sameKey = (name: string) => name.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/**
 * The stations to tune through at (lat, lon), from what the directory gave
 * for here and for the country: the local first, most listened to first,
 * then the rest likewise, each station once.
 */
export function pickStations(rows: readonly DirectoryStation[], lat: number, lon: number, most = MOST_STATIONS): Station[] {
  const seenUrl = new Set<string>(), seenName = new Set<string>();
  const candidates: Array<Station & { clicks: number }> = [];
  for (const r of [...rows].sort((a, b) => b.clickcount - a.clickcount)) {
    const url = r.url_resolved?.trim() ?? '';
    if (r.lastcheckok !== 1 || !url.startsWith('https://') || !PLAYABLE.test(r.codec ?? '') || /\.m3u8?(\?|$)|\.pls(\?|$)/i.test(url)) continue;
    const name = stationName(r.name ?? '');
    const key = sameKey(name);
    if (key === '' || seenUrl.has(url) || seenName.has(key)) continue;
    seenUrl.add(url);
    seenName.add(key);
    const local = r.geo_lat !== null && r.geo_long !== null && km(lat, lon, r.geo_lat, r.geo_long) <= LOCAL_KM;
    candidates.push({ name, url, local, clicks: r.clickcount });
  }
  const local = candidates.filter(c => c.local).slice(0, MOST_LOCAL);
  const rest = candidates.filter(c => !local.includes(c));
  return [...local, ...rest].slice(0, most).map(({ name, url, local: l }) => ({ name, url, local: l }));
}

/** The next on the dial after `current` (−1 off): each station in turn, then off. */
export function nextStation(current: number, count: number): number {
  if (count === 0) return -1;
  return current + 1 >= count ? -1 : current + 1;
}
